import type { Principal, PrincipalAndRole, Role } from '../../../commonTypes.tsx';
import type { SpecificAccessDetailsProps } from '../specificAccessDetails/SpecificAccessDetails.tsx';
import type { SpecificAccessRowProps } from '../specificAccessDetails/specificAccessRow/SpecificAccessRow.tsx';
import type { PrincipalsMap } from '../../../../controller/principalsMap.ts';
import type { useUpdateContentDirectRestrictionsMutation$data as MutationData } from '../../../../controller/mutations/__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';

import type { SpecificAccessLiveMutationArgs } from './specificAccessDialogTypes.ts';

export function getNonNullSpecificAccessPrincipals(
	specificAccessDetails?: SpecificAccessDetailsProps,
): SpecificAccessRowProps[] {
	return (specificAccessDetails?.principals ?? []).filter(
		(p): p is NonNullable<typeof p> => p !== null,
	);
}

export type SpecificAccessLiveDelta = {
	removeAll: boolean;
	removals: Set<string>;
	reassignments: Map<string, Role>;
};

export function createEmptySpecificAccessLiveDelta(): SpecificAccessLiveDelta {
	return {
		removeAll: false,
		removals: new Set(),
		reassignments: new Map(),
	};
}

export function applySpecificAccessLiveDelta({
	basePrincipalsMap,
	currentUserId,
	liveDelta,
}: {
	basePrincipalsMap: PrincipalsMap;
	currentUserId: string;
	liveDelta: SpecificAccessLiveDelta;
}): PrincipalsMap {
	const map: PrincipalsMap = new Map(basePrincipalsMap);

	if (liveDelta.removeAll) {
		for (const id of map.keys()) {
			if (id !== currentUserId) {
				map.delete(id);
			}
		}
	} else {
		for (const [id, role] of liveDelta.reassignments) {
			const existingEntry = map.get(id);
			if (existingEntry) {
				map.set(id, { ...existingEntry, role, isNewOrUpdatedPrincipal: true });
			}
		}

		for (const id of liveDelta.removals) {
			map.delete(id);
		}
	}

	return map;
}

export function createPrincipalsMapFromSpecificAccessRows({
	principals,
}: {
	principals: ReadonlyArray<{
		principalDetails: {
			principal: Principal;
			role: Role;
		};
	}>;
}): PrincipalsMap {
	const map: PrincipalsMap = new Map();
	for (const row of principals) {
		map.set(row.principalDetails.principal.id, {
			role: row.principalDetails.role,
			principal: row.principalDetails.principal,
			isNewOrUpdatedPrincipal: false,
		});
	}
	return map;
}

export function removeAllPrincipalsFromLiveDelta({
	liveDelta,
}: {
	liveDelta: SpecificAccessLiveDelta;
}): SpecificAccessLiveDelta {
	return {
		...liveDelta,
		removeAll: true,
		removals: new Set(),
		reassignments: new Map(),
	};
}

export function removePrincipalFromLiveDelta({
	liveDelta,
	principalId,
}: {
	liveDelta: SpecificAccessLiveDelta;
	principalId: string;
}): SpecificAccessLiveDelta {
	const removals = new Set(liveDelta.removals);
	removals.add(principalId);
	const reassignments = new Map(liveDelta.reassignments);
	reassignments.delete(principalId);
	return { ...liveDelta, removals, reassignments };
}

export function reassignPrincipalInLiveDelta({
	liveDelta,
	principalId,
	role,
}: {
	liveDelta: SpecificAccessLiveDelta;
	principalId: string;
	role: Role;
}): SpecificAccessLiveDelta {
	return {
		...liveDelta,
		reassignments: new Map(liveDelta.reassignments).set(principalId, role),
	};
}

type RestrictionsList = NonNullable<
	NonNullable<NonNullable<AppliedRestrictions['added']>['edit']>['restrictions']
>;

type RestrictionResult =
	| {
			restrictions?: RestrictionsList | null;
	  }
	| null
	| undefined;

type AppliedRestrictions = NonNullable<
	NonNullable<
		NonNullable<
			MutationData['confluence_updateContentDirectRestrictions']
		>['contentDirectRestrictions']
	>['applied']
>;

/**
 * Collects principal IDs from a live mutation restriction result, filtering out nulls.
 */
export function collectRestrictionIds(result: RestrictionResult): Set<string> {
	const ids = new Set<string>();
	for (const restriction of result?.restrictions ?? []) {
		if (restriction?.principalId) {
			ids.add(restriction.principalId);
		}
	}
	return ids;
}

/**
 * Collects all restriction ID sets from an applied restrictions object in one pass.
 * This consolidates the four individual collectRestrictionIds calls.
 */
export function getAppliedRestrictionSets(applied: AppliedRestrictions): {
	addedEdit: Set<string>;
	addedView: Set<string>;
	removedEdit: Set<string>;
	removedView: Set<string>;
} {
	return {
		addedEdit: collectRestrictionIds(applied.added?.edit),
		addedView: collectRestrictionIds(applied.added?.view),
		removedEdit: collectRestrictionIds(applied.removed?.edit),
		removedView: collectRestrictionIds(applied.removed?.view),
	};
}

/**
 * Derives the effective role for a principal based on the mutation payload.
 * Returns null when no change is found.
 */
export function deriveRoleFromApplied({
	applied,
	principalId,
}: {
	applied: AppliedRestrictions;
	principalId: string;
}): Role | null {
	const { addedEdit, addedView, removedEdit, removedView } = getAppliedRestrictionSets(applied);

	if (addedEdit.has(principalId)) {
		return 'can-edit';
	}

	if (addedView.has(principalId)) {
		return 'can-view';
	}

	if (removedEdit.has(principalId) || removedView.has(principalId)) {
		return 'no-access';
	}

	return null;
}

/**
 * Reconciles the local live delta with the mutation response from the server.
 *
 * The server payload encodes which principals were added/removed at each access level.
 * We interpret those changes to keep the live dialog state consistent with the
 * source of truth, without forcing a refetch.
 */
export function applyMutationPayloadToLiveDelta({
	liveDelta,
	payload,
	mutationArgs,
}: {
	liveDelta: SpecificAccessLiveDelta;
	payload: NonNullable<MutationData['confluence_updateContentDirectRestrictions']>;
	mutationArgs: SpecificAccessLiveMutationArgs;
}): SpecificAccessLiveDelta {
	const applied = payload.contentDirectRestrictions?.applied;
	if (!applied) {
		return liveDelta;
	}

	if (mutationArgs.type === 'remove-all') {
		return removeAllPrincipalsFromLiveDelta({ liveDelta });
	}

	const derivedRole = deriveRoleFromApplied({
		applied,
		principalId: mutationArgs.principalId,
	});

	if (!derivedRole) {
		return liveDelta;
	}

	if (derivedRole === 'no-access') {
		return removePrincipalFromLiveDelta({ liveDelta, principalId: mutationArgs.principalId });
	}

	const reassigned = reassignPrincipalInLiveDelta({
		liveDelta,
		principalId: mutationArgs.principalId,
		role: derivedRole,
	});

	if (!reassigned.removals.has(mutationArgs.principalId)) {
		return reassigned;
	}

	const removals = new Set(reassigned.removals);
	removals.delete(mutationArgs.principalId);
	return {
		...reassigned,
		removals,
	};
}

export function shouldIncludeInvitesForSpecificAccessLiveMutation({
	principals,
	liveDelta,
	principalsPendingAccess,
	shouldMutationIncludeInvites,
}: {
	principals: PrincipalsMap;
	liveDelta: SpecificAccessLiveDelta;
	principalsPendingAccess: PrincipalAndRole[];
	shouldMutationIncludeInvites: (args: {
		principals: PrincipalsMap;
		removals: Set<string>;
		principalsPendingAccess: PrincipalAndRole[];
		removeAll: boolean;
	}) => boolean;
}): boolean {
	return shouldMutationIncludeInvites({
		principals,
		removals: liveDelta.removals,
		principalsPendingAccess,
		removeAll: liveDelta.removeAll,
	});
}
